import { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import { applyDeferredNow, UPDATE_DEFERRED_EVENT } from "@/lib/update-gate";
import { applyServiceWorkerUpdate } from "@/lib/sw-register";

/** How often to re-check the registration for a waiting worker. */
const WAITING_POLL_MS = 30_000;

/**
 * TECH-JOB-SCREEN §8 item 1 + FIELD RELIABILITY — the update nag.
 *
 * Two triggers, one banner:
 *
 *   1. DEFERRED updates (the original path): a new build installed while
 *      the tech had unsaved entries, so the update gate parked it and
 *      fired UPDATE_DEFERRED_EVENT. The gate self-applies at the next
 *      safe moment; the banner lets the tech force it sooner.
 *
 *   2. WAITING workers (field incident: stale cached bundles). A worker
 *      can sit in `waiting` without the gate ever hearing about it —
 *      the statechange listener didn't fire (iOS PWA force-quit between
 *      install and adoption), the silent SKIP_WAITING message was lost,
 *      or the page loaded after the worker finished installing. We poll
 *      the registration (mount, focus/visibility, every 30s) and nag as
 *      soon as `reg.waiting` exists, so no tech unknowingly runs a
 *      week-old bundle again.
 *
 * The banner is persistent (no dismiss) and the whole strip is the
 * button — "tap to update" means tap ANYWHERE.
 */
export function UpdateBanner() {
    const [deferred, setDeferred] = useState(false);
    const [waiting, setWaiting] = useState(false);
    // #683 — a tap must LOOK like it did something. Without feedback the
    // strip read as a dead button while the SW work happened invisibly.
    const [busy, setBusy] = useState(false);
    const tapped = useRef(false);

    useEffect(() => {
        const handler = () => setDeferred(true);
        window.addEventListener(UPDATE_DEFERRED_EVENT, handler);
        return () => window.removeEventListener(UPDATE_DEFERRED_EVENT, handler);
    }, []);

    useEffect(() => {
        if (!("serviceWorker" in navigator)) return;
        let cancelled = false;
        const check = async () => {
            try {
                const reg = await navigator.serviceWorker.getRegistration();
                if (!cancelled) setWaiting(!!reg?.waiting);
            } catch {
                // Registration lookup failed — keep the current state.
            }
        };
        void check();
        const interval = setInterval(() => void check(), WAITING_POLL_MS);
        const onFocus = () => void check();
        const onVisibility = () => {
            if (document.visibilityState === "visible") void check();
        };
        window.addEventListener("focus", onFocus);
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            cancelled = true;
            clearInterval(interval);
            window.removeEventListener("focus", onFocus);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, []);

    const handleUpdate = useCallback(() => {
        // #683 — this used to be `if (!applyDeferredNow()) applyServiceWorkerUpdate()`,
        // so a gate holding a STALE closure (its captured worker had gone
        // redundant) posted SKIP_WAITING into the void, returned true, and the
        // live-waiting-worker path never ran. The tap did nothing, forever.
        //
        // Now the tap does BOTH, unconditionally: flush the gate (harmless
        // when empty) and then always drive whatever worker is really
        // waiting — which ends in a reload on every branch, including
        // unregistering a worker that refuses to adopt.
        // The guard is a ref, not the state value: a state updater must stay
        // pure (StrictMode double-invokes it), and two fast taps can land in
        // the same render before `busy` has flushed.
        if (tapped.current) return;
        tapped.current = true;
        setBusy(true);
        applyDeferredNow();
        void applyServiceWorkerUpdate();
    }, []);

    if (!deferred && !waiting) return null;

    return (
        <button
            type="button"
            role="status"
            data-testid="update-nag"
            onClick={handleUpdate}
            disabled={busy}
            aria-busy={busy}
            className="fixed inset-x-0 top-0 z-9998 flex w-full items-center justify-center gap-3 border-b border-primary/40 bg-primary px-4 py-2.5 text-left shadow-md"
        >
            <RefreshCw className={`h-4 w-4 shrink-0 text-primary-foreground${busy ? " animate-spin" : ""}`} />
            <span className="text-sm font-semibold text-primary-foreground">
                {busy ? "Updating…" : "New version available — tap to update"}
            </span>
            {!busy && deferred && (
                <span className="hidden sm:inline text-xs text-primary-foreground/80">
                    (applies automatically after you save)
                </span>
            )}
        </button>
    );
}
